<script setup lang="ts">
import { ref, computed } from 'vue';
import type { BulkPublicStatus } from './types';

const props = defineProps<{
  status: BulkPublicStatus;
  canManage: boolean;
}>();

const emit = defineEmits<{ (e: 'bulk-make-public', makePublic: boolean): void }>();

const working = ref(false);
const pendingMakePublic = ref<boolean | null>(null);

const total = computed(() => props.status.total ?? 0);
const publicCount = computed(() => props.status.public ?? 0);
const allPublic = computed(() => total.value > 0 && publicCount.value === total.value);

const lastResult = computed(() => {
  if (props.status.affected === undefined) {
    return null;
  }
  return { affected: props.status.affected, skipped: props.status.skipped ?? 0 };
});

function requestToggle(event: Event) {
  // Revert the visual change until confirmed.
  (event.target as HTMLInputElement).checked = allPublic.value;
  pendingMakePublic.value = !allPublic.value;
}

function cancel() {
  pendingMakePublic.value = null;
}

function confirm() {
  if (pendingMakePublic.value === null) {
    return;
  }
  working.value = true;
  emit('bulk-make-public', pendingMakePublic.value);
  setTimeout(() => {
    working.value = false;
    pendingMakePublic.value = null;
  }, 800);
}
</script>

<template>
  <section class="pp-section pp-bulk-public">
    <div class="pp-section-title">Bulk make public this collection child</div>

    <p class="small text-muted mb-2">
      Add or remove the Public ePLANETe relationship for every node contextualised
      directly under this collection.
    </p>

    <div class="d-flex align-items-center gap-3 flex-wrap">
      <div class="form-check form-switch m-0">
        <input
          id="pp-bulk-public"
          class="form-check-input"
          type="checkbox"
          role="switch"
          :checked="allPublic"
          :disabled="!canManage || working || total === 0"
          @change="requestToggle"
        />
        <label class="form-check-label" for="pp-bulk-public">
          {{ allPublic ? 'All public' : 'Make all public' }}
        </label>
      </div>

      <span class="badge bg-light text-dark border">
        {{ publicCount }} / {{ total }} public
      </span>
    </div>

    <!-- Confirmation -->
    <div v-if="pendingMakePublic !== null" class="alert alert-warning mt-3 py-2 px-3 small">
      <p class="mb-2">
        {{ pendingMakePublic ? 'Make public' : 'Make private' }}
        {{ pendingMakePublic ? status.private : publicCount }} node(s) under this collection?
        <br />
        <span class="text-muted">
          Nodes already in the target state are left unchanged. A node also needs to be
          workflow-published to be visible to anonymous visitors.
        </span>
      </p>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm btn-warning" :disabled="working" @click="confirm">
          <span v-if="working" class="spinner-border spinner-border-sm me-2" role="status"></span>
          Confirm
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="working" @click="cancel">
          Cancel
        </button>
      </div>
    </div>

    <!-- Result feedback -->
    <div v-else-if="lastResult" class="small text-muted mt-2">
      <i class="bi bi-check-circle text-success me-1"></i>
      {{ lastResult.affected }} updated<span v-if="lastResult.skipped">, {{ lastResult.skipped }} skipped</span>.
    </div>
  </section>
</template>
